import {
  Badge,
  Box,
  Card,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Title as ChartTitle,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import isBetween from "dayjs/plugin/isBetween";
import { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import { Activity, ChartBar, Users, TrendingUp } from "tabler-icons-react";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ChartTitle,
  Tooltip,
  Legend,
  Filler,
);

dayjs.extend(isBetween);
dayjs.locale("fr");

const MetricCard = ({ title, value, subtitle, icon, color }) => (
  <Card padding="md" radius="md" withBorder>
    <Group position="apart" mb="xs">
      <ThemeIcon color={color} variant="light" size="lg">
        {icon}
      </ThemeIcon>
    </Group>
    <Text size="xs" color="dimmed" transform="uppercase" weight={600} mb={4}>
      {title}
    </Text>
    <Text size="xl" weight={700} mb={4}>
      {value}
    </Text>
    {subtitle && (
      <Text size="xs" color="dimmed">
        {subtitle}
      </Text>
    )}
  </Card>
);

const EngagedClientActivityChart = ({
  activityData,
  isLoading,
  dateRange,
  periodFilter,
  totalEngagedClients,
}) => {
  const chartData = useMemo(() => {
    const data = activityData || {};
    const totalActivities = data.total_activities || 0;
    const activitiesByType = data.activities_by_type || {};

    const { start, end } = dateRange;

    // Calculer le nombre de semaines dans la période
    const totalDays = end.diff(start, "day") + 1;
    const weeksInPeriod = Math.max(1, totalDays / 7);

    // Préparer les données pour le graphique par type d'activité
    const labels = [];
    const chartValues = [];
    let topFeature = { name: "Aucune", count: 0 };

    Object.keys(activitiesByType).forEach((key) => {
      const typeData = activitiesByType[key];
      const count = typeData.count || 0;
      labels.push(typeData.name || key);
      chartValues.push(count);

      if (count > topFeature.count) {
        topFeature = {
          name: typeData.name || key,
          count: count,
        };
      }
    });

    // On utilise le nombre total de clients engagés du funnel
    const activeClients = totalEngagedClients || 0;
    
    const avgPerClient = activeClients > 0 ? totalActivities / activeClients : 0;
    const avgPerClientPerWeek = (avgPerClient / weeksInPeriod).toFixed(1);

    return {
      labels,
      chartValues,
      totalActivities,
      activeClients,
      avgPerClientPerWeek,
      topFeature,
      weeksInPeriod: weeksInPeriod.toFixed(1)
    };
  }, [activityData, dateRange, periodFilter, totalEngagedClients]);

  const barChartConfig = {
    labels: chartData.labels,
    datasets: [
      {
        label: "Activités par type",
        data: chartData.chartValues,
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 400);
          gradient.addColorStop(0, "rgba(168, 85, 247, 0.85)"); // grape
          gradient.addColorStop(1, "rgba(168, 85, 247, 0.2)");
          return gradient;
        },
        borderColor: "#a855f7",
        borderWidth: 1.5,
        borderRadius: 4,
      },
    ],
  };

  const barChartOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      title: { display: false },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        padding: 12,
        titleFont: { size: 13 },
        bodyFont: { size: 13 },
        displayColors: false,
        callbacks: {
          label: (context) => `${context.parsed.y} activité(s)`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false, drawBorder: false },
        ticks: { maxTicksLimit: 10, color: "#9ca3af" },
      },
      y: {
        beginAtZero: true,
        grid: { color: "#f3f4f6", borderDash: [5, 5], drawBorder: false },
        ticks: { stepSize: 1, color: "#9ca3af" },
      },
    },
    maintainAspectRatio: false,
    interaction: {
      mode: "nearest",
      axis: "x",
      intersect: false,
    },
  };

  return (
    <Stack spacing="md">
      {/* Métriques */}
      <SimpleGrid
        cols={4}
        spacing="md"
        breakpoints={[
          { maxWidth: "md", cols: 2 },
          { maxWidth: "sm", cols: 1 },
        ]}
      >
        <MetricCard
          title="Moy. par client/semaine"
          value={chartData.avgPerClientPerWeek}
          subtitle={`Basé sur ${chartData.activeClients} client(s) engagé(s)`}
          icon={<TrendingUp size={20} />}
          color="grape"
        />
        <MetricCard
          title="Total activités"
          value={chartData.totalActivities}
          subtitle={`Sur ${chartData.weeksInPeriod} semaine(s)`}
          icon={<Activity size={20} />}
          color="blue"
        />
        <MetricCard
          title="Clients Engagés"
          value={chartData.activeClients}
          subtitle="Actuellement dans ce statut"
          icon={<Users size={20} />}
          color="teal"
        />
        <MetricCard
          title="Top Fonctionnalité"
          value={chartData.topFeature.count > 0 ? `${chartData.topFeature.count} act.` : "—"}
          subtitle={chartData.topFeature.count > 0 ? chartData.topFeature.name : "Aucune donnée"}
          icon={<ChartBar size={20} />}
          color="orange"
        />
      </SimpleGrid>

      {/* Graphique */}
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Group position="apart" mb="md">
          <Text size="lg" weight={700}>
            Répartition des activités
          </Text>
          <Badge variant="light" color="grape" size="lg">
            {chartData.totalActivities} activités au total
          </Badge>
        </Group>

        <div style={{ height: "300px" }}>
          {isLoading ? (
            <Box
              h="100%"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Loader size="lg" />
            </Box>
          ) : chartData.totalActivities === 0 ? (
            <Box
              h="100%"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Text color="dimmed" size="sm">
                Aucune activité de client engagé sur cette période
              </Text>
            </Box>
          ) : (
            <Bar options={barChartOptions} data={barChartConfig} />
          )}
        </div>

        <Text
          align="center"
          color="dimmed"
          size="xs"
          mt="md"
          transform="uppercase"
          weight={600}
          letterSpacing={0.5}
        >
          Volume d'activités par fonctionnalité
        </Text>
      </Card>
    </Stack>
  );
};

export default EngagedClientActivityChart;
